import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";
import { ConfirmProvider } from '@/components/common/ConfirmDialog';

// Suppress benign errors (ResizeObserver from Tiptap/Radix, cross-origin Script errors)
const BENIGN = [
  'ResizeObserver loop completed with undelivered notifications.',
  'ResizeObserver loop limit exceeded',
  'Script error.',
  'Script error',
];
const suppressError = (e) => {
  const msg = e.message || e.reason?.message || '';
  if (BENIGN.includes(msg)) {
    e.stopImmediatePropagation?.();
    e.preventDefault?.();
    return true;
  }
  return false;
};
// window.onerror fires before React's dev overlay — return true to suppress
window.onerror = (message) => BENIGN.includes(message) ? true : undefined;
window.addEventListener('error', suppressError);
window.addEventListener('unhandledrejection', (e) => {
  const msg = e.reason?.message || String(e.reason || '');
  if (msg === 'Script error.' || msg === 'Script error') {
    e.preventDefault();
  }
});

// ── Register PWA Service Worker ──────────────────────────────────────────────
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then((reg) => {
        console.log('[SW] Registered, scope:', reg.scope);

        // Listen for a new SW installing (update found)
        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          newWorker?.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              window.dispatchEvent(new CustomEvent('sw-update-available'));
            }
          });
        });

        // Option A: Poll for updates every 20 minutes while the app is open
        const UPDATE_INTERVAL_MS = 20 * 60 * 1000;
        setInterval(() => {
          reg.update().catch(() => {});
        }, UPDATE_INTERVAL_MS);

        // Option D: Check for updates when the user brings the app back to the foreground
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') {
            reg.update().catch(() => {});
          }
        });
      })
      .catch((err) => console.warn('[SW] Registration failed:', err));

    // When the SW takes control of this page, reload immediately to get new JS
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      window.location.reload();
    });

    // SW messages (offline queue replay, update notifications, etc.)
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'REPLAY_OFFLINE_QUEUE') {
        window.dispatchEvent(new CustomEvent('pwa-replay-queue'));
      }
      if (event.data?.type === 'SW_UPDATED') {
        window.dispatchEvent(new CustomEvent('sw-update-available'));
      }
    });
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,       // 30 s — serve from cache, refetch in background
      retry: 1,                // one retry on failure
      refetchOnWindowFocus: false, // don't surprise users with re-renders on focus
      refetchOnReconnect: false,   // prevent burst of refetches after brief disconnect
      gcTime: 600_000,             // 10 min — keep cache alive across many navigations
      networkMode: 'offlineFirst', // serve cached data while offline (PWA support)
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <QueryClientProvider client={queryClient}>
    <ConfirmProvider>
      <App />
    </ConfirmProvider>
  </QueryClientProvider>
);
